
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Application } from '@/types/database.types';
import { prepareApplicationDataForSave } from './save-utils';

/**
 * Hook to handle manual saving of application progress
 */
export const useManualSave = (
  application: Application | null,
  setApplication: (app: Application | null) => void,
  calculateProgress: () => number,
  lastSaved: Date | null,
  setLastSaved: (date: Date | null) => void,
  isSaving: boolean,
  setIsSaving: (value: boolean) => void
) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const saveProgress = useCallback(async (sectionData?: Partial<Application>) => {
    if (!application?.id) {
      console.error('No application found to save');
      toast({
        title: 'Error',
        description: 'No application found to save',
        variant: 'destructive',
      });
      throw new Error('No application found to save');
    }

    // Prevent concurrent save operations
    if (isSaving) {
      console.log('[saveProgress] Another save operation is in progress, skipping');
      return; 
    }

    try {
      setIsSaving(true);
      
      const progress = calculateProgress();
      
      if (sectionData) {
        console.log("[saveProgress] Starting save with provided data:", {
          ...sectionData,
          corporateStructure: sectionData.corporate_structure,
          isNonProfit: sectionData.is_non_profit,
          currentPEO: sectionData.current_peo,
          payrollPlatform: sectionData.current_payroll_platform,
          status: sectionData.status // Log status explicitly
        });
      } else {
        console.log("[saveProgress] Autosaving current application state");
      }
      
      console.log("[saveProgress] Current application state:", {
        corpStructure: application.corporate_structure,
        isNonProfit: application.is_non_profit,
        currentPEO: application.current_peo,
        payrollPlatform: application.current_payroll_platform,
        status: application.status // Log status explicitly
      });
      
      // Process data to ensure important fields aren't lost
      const processedSectionData = sectionData ? { ...sectionData } : {};
      
      // Add corporate structure debug log
      if (processedSectionData.corporate_structure) {
        console.log('[saveProgress] Corporate structure before save prep:', processedSectionData.corporate_structure, 
          'type:', typeof processedSectionData.corporate_structure);
      }
      
      // Use the utility function to prepare data for saving
      const updateData = prepareApplicationDataForSave(application, processedSectionData);
      
      // Add progress to the update data
      updateData.progress = progress;
      
      console.log("[saveProgress] Sending prepared data to database:", updateData);
      
      // Ensure request body is smaller by removing any unnecessary fields
      const cleanedUpdateData = { ...updateData };
      Object.keys(cleanedUpdateData).forEach(key => {
        if (cleanedUpdateData[key] === undefined) {
          delete cleanedUpdateData[key];
        }
      });
      
      // Add final corporate structure debug log
      if ('corporate_structure' in cleanedUpdateData) {
        console.log('[saveProgress] Final corporate structure value being sent to DB:', 
          cleanedUpdateData.corporate_structure,
          'type:', typeof cleanedUpdateData.corporate_structure);
      }
      
      // Extra logging for status changes
      if ('status' in cleanedUpdateData) {
        console.log('[saveProgress] Updating application status to:', cleanedUpdateData.status);
      }
      
      // Add timeout for the database operation
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database operation timed out')), 15000);
      });

      // Execute the database operation with timeout
      const dbPromise = supabase
        .from('applications')
        .update(cleanedUpdateData)
        .eq('id', application.id)
        .select()
        .single();

      const { error, data } = await Promise.race([dbPromise, timeoutPromise])
        .then(result => result as typeof dbPromise extends Promise<infer T> ? T : never)
        .catch(error => {
          console.error("[saveProgress] Database operation error:", error);
          throw error;
        });

      if (error) {
        console.error("[saveProgress] Error during save:", error);
        throw error;
      }

      console.log("[saveProgress] Save response data:", data);

      // Notify about application submission
      if (updateData.status === 'submitted' && sectionData?.status === 'submitted') {
        console.log("[saveProgress] Application submitted, sending notification");
        
        try {
          console.log("[saveProgress] Invoking notify-application function");
          
          // Use a timeout for the notification function
          const notifyPromise = supabase.functions.invoke('notify-application', {
            body: {
              applicantEmail: application.email || '',
              applicantName: `${application.first_name || ''} ${application.last_name || ''}`.trim(),
              applicationId: application.id
            }
          });
          
          // Set a shorter timeout for notification - this should not block the user experience
          const notifyTimeoutPromise = new Promise((resolve) => {
            setTimeout(() => {
              console.log("[saveProgress] Notification request sent, continuing with update");
              resolve(null);
            }, 3000);
          });
          
          await Promise.race([notifyPromise, notifyTimeoutPromise]);
          console.log("[saveProgress] Notification process complete");
          
          // The navigation is now handled by the ReviewAndSubmit component with a useEffect
        } catch (notifyError) {
          console.error("[saveProgress] Error notifying about application:", notifyError);
          // Continue even if notification fails - don't block the submission
        }
      }
      
      // Create a merged application object with all the original data
      // plus any data returned from the database and any new section data
      // Fix the TypeScript error by ensuring all spread objects are actual objects
      const mergedApplication: Application = {
        ...application,
        ...(data as object || {}),
        ...(processedSectionData as object || {}),
        progress: progress,
      };
      
      console.log("[saveProgress] Setting updated application state:", {
        corporateStructure: mergedApplication.corporate_structure,
        isNonProfit: mergedApplication.is_non_profit,
        currentPEO: mergedApplication.current_peo,
        payrollPlatform: mergedApplication.current_payroll_platform,
        status: mergedApplication.status // Log status explicitly
      });
      
      // Update application state with the merged data
      setApplication(mergedApplication);
      
      setLastSaved(new Date());
      
      // Show success toast for manual saves (not for autosaves)
      if (sectionData && sectionData.status !== 'submitted') { // Don't show this toast on submission
        toast({
          title: 'Progress saved',
          description: 'Your application progress has been saved successfully.',
        });
      }
      
      console.log("[saveProgress] Progress save successful");
      
      return true;
    } catch (error: any) {
      console.error("[saveProgress] Save progress error:", error);
      toast({
        title: 'Error saving progress',
        description: error.message || 'There was a problem saving your progress. Please try again.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [application, calculateProgress, navigate, setApplication, setIsSaving, setLastSaved, toast, isSaving]);

  return {
    saveProgress
  };
};
